import parsePhoneNumberFromString from 'libphonenumber-js'

/**
 * number_service returns E.164 digits with NO leading '+' ("919891859683").
 * libphonenumber cannot parse that without a default region, so it returned
 * undefined and both halves of the cell degraded silently: the flag fell back to
 * US for an Indian number, and the number rendered as raw ungrouped digits.
 *
 * Restoring the '+' is enough to parse it, because the value really is E.164 -
 * it is only the prefix that is missing. A value that already has one is left
 * alone, and a blank stays blank rather than becoming a bare '+'.
 */
export const toE164 = (number: string): string => {
  const trimmed = number.trim()
  if (!trimmed) return ''
  if (trimmed.startsWith('+')) return trimmed
  const digits = trimmed.replace(/\D/g, '')
  return digits ? `+${digits}` : ''
}

/**
 * Neo's `Flag` defaults `iso` to 'US', so passing undefined for an unknown
 * number renders a confident US flag - exactly the bug this helper exists to
 * fix. 'unknown' is the component's own sentinel (it skips the clip path and
 * draws a neutral placeholder), which keeps row alignment while claiming
 * nothing about the number.
 */
export const UNKNOWN_FLAG_ISO = 'unknown'

export interface SandboxNumberDisplay {
  /** Grouped E.164 for display, or the original value if it will not parse. */
  formatted: string
  /** ISO country for the flag. `UNKNOWN_FLAG_ISO` when it cannot be resolved. */
  countryIso: string
}

/**
 * `countryIso` from the API is the fallback, not the primary: the parsed number
 * is authoritative, but an unparseable value should still show the right flag
 * rather than defaulting to US.
 */
export const getSandboxNumberDisplay = (
  number: string,
  countryIso?: string,
): SandboxNumberDisplay => {
  const parsed = parsePhoneNumberFromString(toE164(number))
  return {
    formatted: parsed?.formatInternational() ?? number,
    countryIso: parsed?.country ?? countryIso?.toUpperCase() ?? UNKNOWN_FLAG_ISO,
  }
}
